import React, { createContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { identifyUser, resetUser } from '../services/posthog';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to fetch current user data from server
  const refreshUser = useCallback(async () => {
    try {
      console.log('[AuthContext] Refreshing user data...');
      const response = await api.get('/auth/me');
      console.log('[AuthContext] User data refreshed:', response.data.user);
      setUser(response.data.user);
      // Identify user in PostHog analytics (handles returning users)
      identifyUser(response.data.user);
      setError(null);
      return response.data.user;
    } catch (err) {
      console.log('[AuthContext] User refresh failed:', err.response?.status, err.message);
      setUser(null);
      setError(null);
      throw err;
    }
  }, []);

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      console.log('[AuthContext] Starting initial auth check...');
      try {
        await refreshUser();
        console.log('[AuthContext] Initial auth check: user is logged in');
      } catch (err) {
        // Auth check failed, user is not logged in - this is normal
        console.log('[AuthContext] Initial auth check: user not logged in');
      } finally {
        console.log('[AuthContext] Initial auth check complete, setting loading to false');
        setLoading(false);
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      setLoading(true);
      setError(null);
      console.log('AuthContext: Attempting login with:', username);
      console.log('API base URL:', api.defaults.baseURL);
      const response = await api.post('/auth/login', { username, password });
      console.log('AuthContext: Login response:', response.data);
      setUser(response.data.user);
      // Identify user in PostHog analytics
      identifyUser(response.data.user);
      console.log('AuthContext: User set to:', response.data.user);
      return response.data;
    } catch (err) {
      console.error('AuthContext: Login error:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      const errorMessage = err.response?.data?.message || err.message || 'Login failed';
      console.log('AuthContext: Setting error to:', errorMessage);
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await api.post('/auth/logout');
      setUser(null);
      setError(null);
      // Reset PostHog user identity
      resetUser();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const hasPermission = useCallback((permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  }, [user]);

  const hasRole = useCallback((role) => {
    if (!user) return false;
    return user.role === role;
  }, [user]);

  const isAuthenticated = !!user;

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    refreshUser,
    isAuthenticated,
    hasPermission,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
